import { useState, useEffect } from 'react';
import { CreatePersonWithRelationData } from '@/hooks/usePersonProfiles';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNativeConfirmation } from '@/hooks/useNativeConfirmation';
import { useToast } from '@/hooks/use-toast';

interface CreatePersonFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePersonWithRelationData) => Promise<void>;
  initialData?: Partial<CreatePersonWithRelationData> & { id?: string };
  title?: string;
  onDelete?: (personId: string) => Promise<void>;
}

export const CreatePersonForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData,
  title = "Ajouter une personne",
  onDelete
}: CreatePersonFormProps) => {
  const isMobile = useIsMobile();
  const { showConfirmation } = useNativeConfirmation();
  const { toast } = useToast();
  const [formData, setFormData] = useState<CreatePersonWithRelationData>({
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    category: initialData?.category || 'friends',
    date_of_birth: initialData?.date_of_birth || '',
    gender: initialData?.gender,
    phone_primary: initialData?.phone_primary || '',
    email: initialData?.email || '',
    address: initialData?.address || '',
    notes: initialData?.notes || '',
    emergency_contact: initialData?.emergency_contact || false,
    medical_speciality: initialData?.medical_speciality || '',
    relationship_type: initialData?.relationship_type || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const isEditing = !!(initialData?.id);

  // Update formData when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        category: initialData.category || 'friends',
        date_of_birth: initialData.date_of_birth || '',
        gender: initialData.gender,
        phone_primary: initialData.phone_primary || '',
        email: initialData.email || '',
        address: initialData.address || '',
        notes: initialData.notes || '',
        emergency_contact: initialData.emergency_contact || false,
        family_role: initialData.family_role,
        medical_speciality: initialData.medical_speciality || '',
        relationship_type: initialData.relationship_type || '',
      });
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name) return;

    setIsSubmitting(true);
    try {
      // Clean up empty date fields before submission
      const cleanedData = {
        ...formData,
        date_of_birth: formData.date_of_birth || undefined,
      };
      await onSubmit(cleanedData);
      onClose();
      setFormData({
        first_name: '',
        last_name: '',
        category: 'friends',
        date_of_birth: '',
        phone_primary: '',
        email: '',
        address: '',
        notes: '',
        emergency_contact: false,
        medical_speciality: '',
        relationship_type: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!initialData?.id || !onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(initialData.id);
      toast({
        title: "Personne supprimée",
        description: "La personne a été supprimée avec succès.",
      });
      onClose();
    } catch (error) {
      console.error('❌ Error deleting person:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer la personne.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = async () => {
    // Use native confirmation on mobile Capacitor app
    const isCapacitor = typeof window !== 'undefined' && !!(window as any).Capacitor;
    
    if (isMobile && isCapacitor) {
      const confirmed = await showConfirmation({
        title: "Supprimer cette personne ?",
        message: "Cette action est irréversible. La personne sera définitivement supprimée de vos contacts.",
        okButtonTitle: "Supprimer",
        cancelButtonTitle: "Annuler"
      });
      
      if (confirmed) {
        handleDelete();
      }
    } else {
      // On web, the AlertDialog handles the confirmation
      // This function is called from within the AlertDialog
      handleDelete();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">Prénom *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="last_name">Nom *</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="category">Catégorie</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                type="button"
                variant={formData.category === 'family' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormData({ ...formData, category: 'family' })}
                className="h-8 px-3"
              >
                👨‍👩‍👧‍👦 Famille
              </Button>
              <Button
                type="button"
                variant={formData.category === 'extended_family' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormData({ ...formData, category: 'extended_family' })}
                className="h-8 px-3"
              >
                👪 Famille étendue
              </Button>
              <Button
                type="button"
                variant={formData.category === 'friends' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormData({ ...formData, category: 'friends' })}
                className="h-8 px-3"
              >
                👥 Amis
              </Button>
              <Button
                type="button"
                variant={formData.category === 'medical_other' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormData({ ...formData, category: 'medical_other' })}
                className="h-8 px-3"
              >
                🏥 Médical/Autres
              </Button>
            </div>
          </div>

          {/* Family Role - only show for family category */}
          {formData.category === 'family' && (
            <div>
              <Label htmlFor="family_role">Rôle dans la famille</Label>
              <Select value={formData.family_role || ''} onValueChange={(value: any) => setFormData({ ...formData, family_role: value || undefined })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="spouse">Conjoint</SelectItem>
                  <SelectItem value="child">Enfant</SelectItem>
                  <SelectItem value="grandparent">Grand-parent</SelectItem>
                  <SelectItem value="guardian">Tuteur/Tutrice</SelectItem>
                  <SelectItem value="pet">Animal de compagnie</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Extended Family Role - using relationship_type field with dropdown */}
          {formData.category === 'extended_family' && (
            <div>
              <Label htmlFor="relationship_type">Relation familiale</Label>
              <Select value={formData.relationship_type || ''} onValueChange={(value: any) => setFormData({ ...formData, relationship_type: value || undefined })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une relation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="grandfather">Grand-père</SelectItem>
                  <SelectItem value="grandmother">Grand-mère</SelectItem>
                  <SelectItem value="uncle">Oncle</SelectItem>
                  <SelectItem value="aunt">Tante</SelectItem>
                  <SelectItem value="brother">Frère</SelectItem>
                  <SelectItem value="sister">Sœur</SelectItem>
                  <SelectItem value="male_cousin">Cousin</SelectItem>
                  <SelectItem value="female_cousin">Cousine</SelectItem>
                  <SelectItem value="nephew">Neveu</SelectItem>
                  <SelectItem value="niece">Nièce</SelectItem>
                  <SelectItem value="stepfather">Beau-père</SelectItem>
                  <SelectItem value="stepmother">Belle-mère</SelectItem>
                  <SelectItem value="brother_in_law">Beau-frère</SelectItem>
                  <SelectItem value="sister_in_law">Belle-sœur</SelectItem>
                  <SelectItem value="pet">Animal de compagnie</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Medical Speciality - only show for medical_other category */}
          {formData.category === 'medical_other' && (
            <div>
              <Label htmlFor="medical_speciality">Spécialité médicale</Label>
              <Select value={formData.medical_speciality || ''} onValueChange={(value: any) => setFormData({ ...formData, medical_speciality: value || undefined })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une spécialité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medecin_generaliste">Médecin généraliste</SelectItem>
                  <SelectItem value="pediatre">Pédiatre</SelectItem>
                  <SelectItem value="dentiste">Dentiste</SelectItem>
                  <SelectItem value="ophtalmologue">Ophtalmologue</SelectItem>
                  <SelectItem value="cardiologue">Cardiologue</SelectItem>
                  <SelectItem value="dermatologue">Dermatologue</SelectItem>
                  <SelectItem value="pharmacien">Pharmacien</SelectItem>
                  <SelectItem value="psychologue">Psychologue</SelectItem>
                  <SelectItem value="kinesitherapeute">Kinésithérapeute</SelectItem>
                  <SelectItem value="veterinaire">Vétérinaire</SelectItem>
                  <SelectItem value="autre_medical">Autre professionnel médical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Relationship Type - only show for friends category */}
          {formData.category === 'friends' && (
            <div>
              <Label htmlFor="relationship_type">Type de relation</Label>
              <Select value={formData.relationship_type || ''} onValueChange={(value: any) => setFormData({ ...formData, relationship_type: value || undefined })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ami_proche">Ami proche</SelectItem>
                  <SelectItem value="collegue">Collègue</SelectItem>
                  <SelectItem value="voisin">Voisin</SelectItem>
                  <SelectItem value="baby_sitter">Baby-sitter</SelectItem>
                  <SelectItem value="professeur">Professeur</SelectItem>
                  <SelectItem value="entraineur">Entraîneur</SelectItem>
                  <SelectItem value="autre_ami">Autre ami</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_of_birth">Date de naissance</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="text-sm"
              />
            </div>
            <div>
              <Label htmlFor="gender">Genre</Label>
              <Select value={formData.gender || ''} onValueChange={(value: any) => setFormData({ ...formData, gender: value || undefined })}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Homme</SelectItem>
                  <SelectItem value="female">Femme</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                  <SelectItem value="prefer_not_to_say">Préfère ne pas dire</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="phone_primary">Téléphone</Label>
            <Input
              id="phone_primary"
              type="tel"
              value={formData.phone_primary}
              onChange={(e) => setFormData({ ...formData, phone_primary: e.target.value })}
              placeholder="+33 6 12 34 56 78"
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="address">Adresse</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="emergency_contact"
              checked={formData.emergency_contact}
              onCheckedChange={(checked) => setFormData({ ...formData, emergency_contact: checked })}
            />
            <Label htmlFor="emergency_contact">Contact d'urgence</Label>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          {/* Danger Zone - Only show in edit mode */}
          {isEditing && onDelete && (
            <div className="space-y-3 p-4 border rounded-lg bg-gradient-to-r from-destructive/5 to-destructive/10 mb-20">
              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-destructive" />
                <Label className="font-medium text-destructive">Zone de danger</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                La suppression de cette personne supprimera définitivement toutes les données associées.
              </p>
              
              {/* Show different UI based on platform */}
              {isMobile && typeof window !== 'undefined' && !!(window as any).Capacitor ? (
                // Mobile Capacitor: Direct button with native confirmation
                <Button 
                  variant="destructive" 
                  size="sm"
                  className="gap-2"
                  disabled={isDeleting}
                  type="button"
                  onClick={handleDeleteClick}
                >
                  <Trash2 className="h-4 w-4" />
                  {isDeleting ? "Suppression..." : "Supprimer la personne"}
                </Button>
              ) : (
                // Web: AlertDialog for confirmation
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      className="gap-2"
                      disabled={isDeleting}
                      type="button"
                    >
                      <Trash2 className="h-4 w-4" />
                      {isDeleting ? "Suppression..." : "Supprimer la personne"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer cette personne ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cette action est irréversible. La personne sera définitivement supprimée de vos contacts.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDeleteClick}
                        disabled={isDeleting}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {isDeleting ? 'Suppression...' : 'Supprimer définitivement'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          )}
        </form>
        
        {/* Fixed bottom actions */}
        <div className="fixed bottom-0 left-0 right-0 p-4 border-t bg-background space-y-3 z-50">
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.first_name.trim() || !formData.last_name.trim()}
            className="w-full"
            style={{ backgroundColor: 'hsl(var(--coral))' }}
          >
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
          <Button variant="outline" onClick={onClose} className="w-full">
            Annuler
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};